import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Tabs, Tab, Grid, Card, CardContent, Typography, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  Chip, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, CircularProgress, Skeleton,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import AdminHeader from '../../components/AdminHeader/AdminHeader';

const CATEGORY_COLORS: Record<string, string> = {
  health_checkup: '#10b981', webinar: '#3b82f6', workshop: '#8b5cf6', fitness: '#f59e0b',
  screening: '#ef4444', awareness: '#06b6d4', consultation: '#ec4899', donation: '#dc2626',
  recognition: '#f97316', other: '#6b7280',
};

const CATEGORY_LABELS: Record<string, string> = {
  health_checkup: 'Health Checkup', webinar: 'Webinar', workshop: 'Workshop', fitness: 'Fitness',
  screening: 'Screening', awareness: 'Awareness', consultation: 'Consultation', donation: 'Donation',
  recognition: 'Recognition', other: 'Other',
};

const MODE_LABELS: Record<string, string> = { online: 'Online', onsite: 'Onsite', hybrid: 'Hybrid' };

const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b', under_review: '#3b82f6', confirmed: '#10b981',
  rejected: '#ef4444', completed: '#6b7280', cancelled: '#94a3b8',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending', under_review: 'Under Review', confirmed: 'Confirmed',
  rejected: 'Rejected', completed: 'Completed', cancelled: 'Cancelled',
};

interface WellnessEvent {
  id: number; title: string; theme: string; description: string;
  event_date: string; end_date?: string; mode: string; category: string;
  image?: string; visibility: string; is_active: boolean; is_recurring: boolean;
  max_participants?: number; venue: string; meeting_link: string;
  expert_name: string; expert_designation: string;
  registration_count: number; created_at: string;
}

interface WellnessRequest {
  id: number; request_id: string; event: number; event_title: string;
  event_date: string; event_category: string;
  corporate: number; corporate_name: string;
  requested_by: number; requested_by_name: string;
  estimated_participants: number; preferred_date?: string; preferred_mode: string;
  budget_range: string; special_requirements: string;
  status: string; admin_notes: string;
  confirmed_date?: string; confirmed_cost?: number;
  vendor_name: string; vendor_contact: string; expert_name: string; venue_or_link: string;
  created_at: string; updated_at: string;
}

interface Registration {
  id: number; event: number; event_title: string; event_date: string;
  user: number; user_name: string; corporate_name?: string; attended: boolean;
  feedback: string; rating?: number; created_at: string;
}

const VISIBILITY_LABELS: Record<string, string> = { draft: 'Draft', published: 'Published (HR)', confirmed: 'Confirmed (All)' };
const VISIBILITY_COLORS: Record<string, string> = { draft: '#94a3b8', published: '#f59e0b', confirmed: '#10b981' };

const EMPTY_EVENT = {
  title: '', theme: '', description: '', event_date: '', end_date: '',
  mode: 'onsite', category: 'webinar', visibility: 'published', is_active: true, is_recurring: false,
  max_participants: '', venue: '', meeting_link: '', expert_name: '', expert_designation: '',
};

const AdminWellnessCalendar: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });

  // Events state
  const [events, setEvents] = useState<WellnessEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<WellnessEvent | null>(null);
  const [eventForm, setEventForm] = useState<any>({ ...EMPTY_EVENT });
  const [eventImage, setEventImage] = useState<File | null>(null);
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Requests state
  const [requests, setRequests] = useState<WellnessRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestStatusFilter, setRequestStatusFilter] = useState('');
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<WellnessRequest | null>(null);
  const [requestUpdateForm, setRequestUpdateForm] = useState<any>({});
  const [requestUpdating, setRequestUpdating] = useState(false);

  // Registrations state
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [regsLoading, setRegsLoading] = useState(true);

  useEffect(() => { fetchEvents(); }, [calMonth, calYear]);
  useEffect(() => { if (tab === 1) fetchRequests(); }, [tab, requestStatusFilter]);
  useEffect(() => { if (tab === 2) fetchRegistrations(); }, [tab]);

  const fetchEvents = async () => {
    setEventsLoading(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.wellness.events, {
        params: { month: calMonth + 1, year: calYear },
      });
      setEvents(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch { setSnackbar({ open: true, message: 'Failed to load events', severity: 'error' }); }
    finally { setEventsLoading(false); }
  };

  const fetchRequests = async () => {
    setRequestsLoading(true);
    try {
      const params: any = {};
      if (requestStatusFilter) params.status = requestStatusFilter;
      const res = await axiosInstance.get(API_ENDPOINTS.wellness.requests, { params });
      setRequests(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch { setSnackbar({ open: true, message: 'Failed to load requests', severity: 'error' }); }
    finally { setRequestsLoading(false); }
  };

  const fetchRegistrations = async () => {
    setRegsLoading(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.wellness.registrations);
      setRegistrations(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch { setSnackbar({ open: true, message: 'Failed to load registrations', severity: 'error' }); }
    finally { setRegsLoading(false); }
  };

  // Calendar helpers
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(calYear, calMonth, 1).getDay();
  const monthName = new Date(calYear, calMonth).toLocaleString('default', { month: 'long' });

  const getEventsForDay = (day: number) => {
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.event_date === dateStr);
  };

  // Event CRUD
  const openCreateEvent = () => {
    setEditingEvent(null);
    setEventForm({ ...EMPTY_EVENT });
    setEventImage(null);
    setEventDialogOpen(true);
  };

  const openEditEvent = async (ev: WellnessEvent) => {
    try {
      // Fetch full event detail (list only returns minimal fields)
      const res = await axiosInstance.get(API_ENDPOINTS.wellness.eventDetail(ev.id));
      const full = res.data;
      setEditingEvent(full);
      setEventForm({
        title: full.title || '', theme: full.theme || '', description: full.description || '',
        event_date: full.event_date || '', end_date: full.end_date || '',
        mode: full.mode || 'onsite', category: full.category || 'webinar', visibility: full.visibility || 'published',
        is_active: full.is_active ?? true, is_recurring: full.is_recurring ?? false,
        max_participants: full.max_participants || '', venue: full.venue || '', meeting_link: full.meeting_link || '',
        expert_name: full.expert_name || '', expert_designation: full.expert_designation || '',
      });
      setEventImage(null);
      setEventDialogOpen(true);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load event details', severity: 'error' });
    }
  };

  const handleEventSubmit = async () => {
    if (!eventForm.title || !eventForm.event_date) {
      setSnackbar({ open: true, message: 'Title and date are required', severity: 'error' });
      return;
    }
    setEventSubmitting(true);
    try {
      let requestConfig: any;
      let payload: any;

      if (eventImage) {
        // Use FormData when image is present
        const fd = new FormData();
        Object.entries(eventForm).forEach(([k, v]) => {
          if (v !== null && v !== undefined) fd.append(k, String(v));
        });
        fd.append('image', eventImage);
        payload = fd;
        requestConfig = { headers: { 'Content-Type': 'multipart/form-data' } };
      } else {
        // Use JSON when no image — cleaner, avoids FormData string issues
        payload = {
          ...eventForm,
          max_participants: eventForm.max_participants ? Number(eventForm.max_participants) : null,
          end_date: eventForm.end_date || null,
        };
        requestConfig = {};
      }

      if (editingEvent) {
        await axiosInstance.patch(API_ENDPOINTS.wellness.eventDetail(editingEvent.id), payload, requestConfig);
        setSnackbar({ open: true, message: 'Event updated', severity: 'success' });
      } else {
        await axiosInstance.post(API_ENDPOINTS.wellness.events, payload, requestConfig);
        setSnackbar({ open: true, message: 'Event created', severity: 'success' });
      }
      setEventDialogOpen(false);
      fetchEvents();
    } catch (err: any) {
      const msg = err.response?.data?.detail || (typeof err.response?.data === 'object' ? Object.values(err.response.data).flat().join(', ') : 'Failed');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally { setEventSubmitting(false); }
  };

  const handleDeleteEvent = async (id: number) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Event',
      message: 'Are you sure you want to delete this event?',
      onConfirm: async () => {
        try {
          await axiosInstance.delete(API_ENDPOINTS.wellness.eventDetail(id));
          setSnackbar({ open: true, message: 'Event deleted', severity: 'success' });
          fetchEvents();
        } catch { setSnackbar({ open: true, message: 'Failed to delete', severity: 'error' }); }
      },
    });
  };

  // Request update
  const openRequestDialog = (req: WellnessRequest) => {
    setSelectedRequest(req);
    setRequestUpdateForm({
      status: req.status, admin_notes: req.admin_notes,
      confirmed_date: req.confirmed_date || '', confirmed_cost: req.confirmed_cost || '',
      vendor_name: req.vendor_name, vendor_contact: req.vendor_contact,
      expert_name: req.expert_name, venue_or_link: req.venue_or_link,
    });
    setRequestDialogOpen(true);
  };

  const handleRequestUpdate = async () => {
    if (!selectedRequest) return;
    setRequestUpdating(true);
    try {
      await axiosInstance.post(API_ENDPOINTS.wellness.requestUpdateStatus(selectedRequest.id), requestUpdateForm);
      setSnackbar({ open: true, message: 'Request updated', severity: 'success' });
      setRequestDialogOpen(false);
      fetchRequests();
    } catch { setSnackbar({ open: true, message: 'Failed to update', severity: 'error' }); }
    finally { setRequestUpdating(false); }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc', width: '100%' }}>
      <AdminHeader />
      <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventRoundedIcon /> Wellness Calendar Management
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: '1px solid #e2e8f0' }}>
          <Tab label="Events" />
          <Tab label="Service Requests" />
          <Tab label="Registrations" />
        </Tabs>

        {/* ===== TAB 0: EVENTS ===== */}
        {tab === 0 && (
          <Box>
            {/* Month navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }}>
                  <ChevronLeftRoundedIcon />
                </IconButton>
                <Typography variant="h6" fontWeight={600}>{monthName} {calYear}</Typography>
                <IconButton onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }}>
                  <ChevronRightRoundedIcon />
                </IconButton>
              </Box>
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateEvent}
                sx={{ bgcolor: '#06b6d4', '&:hover': { bgcolor: '#0891b2' }, borderRadius: 2, textTransform: 'none' }}>
                Add Event
              </Button>
            </Box>

            {/* Calendar grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <Box key={d} sx={{ bgcolor: '#f8fafc', p: 1, textAlign: 'center', fontWeight: 600, fontSize: 13, color: '#64748b' }}>{d}</Box>
              ))}
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <Box key={`empty-${i}`} sx={{ bgcolor: '#fff', p: 1, minHeight: 70 }} />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dayEvents = getEventsForDay(day);
                const isToday = day === new Date().getDate() && calMonth === new Date().getMonth() && calYear === new Date().getFullYear();
                return (
                  <Box key={day} sx={{ bgcolor: isToday ? '#f0fdfa' : '#fff', p: 1, minHeight: 70 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: isToday ? 700 : 400, color: isToday ? '#0d9488' : '#334155' }}>{day}</Typography>
                    {dayEvents.map(ev => (
                      <Box key={ev.id} sx={{ mt: 0.5, px: 0.5, py: 0.25, borderRadius: 1, bgcolor: `${CATEGORY_COLORS[ev.category]}15`,
                        borderLeft: `3px solid ${CATEGORY_COLORS[ev.category]}`, fontSize: 11, color: '#334155', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        onClick={() => openEditEvent(ev)} title={ev.title}>
                        {ev.title}
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>

            {/* Event list */}
            {eventsLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={80} />)}
              </Box>
            ) : events.length === 0 ? (
              <Typography sx={{ textAlign: 'center', color: '#94a3b8', py: 4 }}>No events this month</Typography>
            ) : (
              <Grid container spacing={2}>
                {events.map(ev => (
                  <Grid size={{ xs: 12, md: 6 }} key={ev.id}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #f1f5f9', borderLeft: `4px solid ${CATEGORY_COLORS[ev.category]}` }}>
                      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={600}>{ev.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{ev.theme}</Typography>
                          {ev.description && (
                            <Typography variant="body2" sx={{ mt: 0.5, color: '#475569', fontSize: 13 }}>
                              {ev.description.length > 100 ? ev.description.slice(0, 100) + '...' : ev.description}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                            <Chip label={ev.event_date} size="small" />
                            <Chip label={CATEGORY_LABELS[ev.category] || ev.category} size="small" sx={{ bgcolor: `${CATEGORY_COLORS[ev.category]}15`, color: CATEGORY_COLORS[ev.category] }} />
                            <Chip label={MODE_LABELS[ev.mode] || ev.mode} size="small" variant="outlined" />
                            <Chip icon={<PeopleRoundedIcon sx={{ fontSize: 14 }} />} label={ev.registration_count} size="small" />
                            <Chip label={VISIBILITY_LABELS[ev.visibility] || ev.visibility} size="small" sx={{ bgcolor: `${VISIBILITY_COLORS[ev.visibility]}15`, color: VISIBILITY_COLORS[ev.visibility], fontWeight: 600 }} />
                            {!ev.is_active && <Chip label="Inactive" size="small" color="default" />}
                          </Box>
                          {(ev.venue || ev.expert_name) && (
                            <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: 12, color: '#64748b' }}>
                              {ev.venue && <span>📍 {ev.venue}</span>}
                              {ev.expert_name && <span>👨‍⚕️ {ev.expert_name}{ev.expert_designation ? ` (${ev.expert_designation})` : ''}</span>}
                              {ev.meeting_link && <span>🔗 {ev.meeting_link}</span>}
                            </Box>
                          )}
                        </Box>
                        <Box>
                          <IconButton size="small" onClick={() => openEditEvent(ev)}><EditRoundedIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleDeleteEvent(ev.id)}><DeleteRoundedIcon fontSize="small" /></IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* ===== TAB 1: REQUESTS ===== */}
        {tab === 1 && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select value={requestStatusFilter} label="Filter by Status" onChange={e => setRequestStatusFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            {requestsLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={50} />)}
              </Box>
            ) : requests.length === 0 ? (
              <Typography sx={{ textAlign: 'center', color: '#94a3b8', py: 4 }}>No service requests</Typography>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Request ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Corporate</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Requested By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Participants</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map(req => (
                      <TableRow key={req.id} hover sx={{ cursor: 'pointer' }} onClick={() => openRequestDialog(req)}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{req.request_id.slice(0, 8)}</TableCell>
                        <TableCell>{req.event_title}</TableCell>
                        <TableCell>{req.corporate_name}</TableCell>
                        <TableCell>{req.requested_by_name}</TableCell>
                        <TableCell>{req.estimated_participants}</TableCell>
                        <TableCell>
                          <Chip label={STATUS_LABELS[req.status] || req.status} size="small"
                            sx={{ bgcolor: `${STATUS_COLORS[req.status]}15`, color: STATUS_COLORS[req.status], fontWeight: 600 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button size="small" onClick={(e) => { e.stopPropagation(); openRequestDialog(req); }}>Manage</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* ===== TAB 2: REGISTRATIONS ===== */}
        {tab === 2 && (
          <Box>
            {regsLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={50} />)}
              </Box>
            ) : registrations.length === 0 ? (
              <Typography sx={{ textAlign: 'center', color: '#94a3b8', py: 4 }}>No registrations</Typography>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 3, width: '100%' }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Corporate</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Attended</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Feedback</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Registered</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {registrations.map(reg => (
                      <TableRow key={reg.id}>
                        <TableCell>{reg.event_title}</TableCell>
                        <TableCell>{reg.event_date}</TableCell>
                        <TableCell>{reg.user_name}</TableCell>
                        <TableCell>{reg.corporate_name || '—'}</TableCell>
                        <TableCell>{reg.attended ? '✓' : '—'}</TableCell>
                        <TableCell>{reg.rating ? `${reg.rating}/5` : '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {reg.feedback || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{new Date(reg.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>

      {/* ===== EVENT DIALOG ===== */}
      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #0f172a, #0e7490)', color: '#fff', fontWeight: 600 }}>
          {editingEvent ? 'Edit Event' : 'Add Event'}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Title *" value={eventForm.title} onChange={e => setEventForm({ ...eventForm, title: e.target.value })} size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Theme" value={eventForm.theme} onChange={e => setEventForm({ ...eventForm, theme: e.target.value })} size="small" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Description" value={eventForm.description} onChange={e => setEventForm({ ...eventForm, description: e.target.value })} multiline rows={3} size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth type="date" label="Event Date *" value={eventForm.event_date} onChange={e => setEventForm({ ...eventForm, event_date: e.target.value })} InputLabelProps={{ shrink: true }} size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth type="date" label="End Date" value={eventForm.end_date} onChange={e => setEventForm({ ...eventForm, end_date: e.target.value })} InputLabelProps={{ shrink: true }} size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select value={eventForm.mode} label="Mode" onChange={e => setEventForm({ ...eventForm, mode: e.target.value })}>
                  <MenuItem value="onsite">Onsite</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="hybrid">Hybrid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={eventForm.category} label="Category" onChange={e => setEventForm({ ...eventForm, category: e.target.value })}>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select value={eventForm.visibility} label="Visibility" onChange={e => setEventForm({ ...eventForm, visibility: e.target.value })}>
                  <MenuItem value="draft">Draft (Admin only)</MenuItem>
                  <MenuItem value="published">Published (HR can see & request)</MenuItem>
                  <MenuItem value="confirmed">Confirmed (All employees)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <input type="file" ref={fileRef} accept="image/*" style={{ display: 'none' }} onChange={e => setEventImage(e.target.files?.[0] || null)} />
              <Button variant="outlined" fullWidth onClick={() => fileRef.current?.click()} sx={{ height: 40 }}>
                {eventImage ? eventImage.name : 'Upload Image'}
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Venue" value={eventForm.venue} onChange={e => setEventForm({ ...eventForm, venue: e.target.value })} size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Meeting Link" value={eventForm.meeting_link} onChange={e => setEventForm({ ...eventForm, meeting_link: e.target.value })} size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Expert Name" value={eventForm.expert_name} onChange={e => setEventForm({ ...eventForm, expert_name: e.target.value })} size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Expert Designation" value={eventForm.expert_designation} onChange={e => setEventForm({ ...eventForm, expert_designation: e.target.value })} size="small" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel control={<Switch checked={eventForm.is_active} onChange={e => setEventForm({ ...eventForm, is_active: e.target.checked })} />} label="Active" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEventDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEventSubmit} disabled={eventSubmitting}
            sx={{ bgcolor: '#06b6d4', '&:hover': { bgcolor: '#0891b2' } }}>
            {eventSubmitting ? <CircularProgress size={20} /> : editingEvent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== REQUEST DIALOG ===== */}
      <Dialog open={requestDialogOpen} onClose={() => setRequestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #0f172a, #0e7490)', color: '#fff', fontWeight: 600 }}>
          Manage Request — {selectedRequest?.event_title}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {selectedRequest && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="text.secondary">Corporate</Typography>
                <Typography fontWeight={500}>{selectedRequest.corporate_name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="text.secondary">Requested By</Typography>
                <Typography fontWeight={500}>{selectedRequest.requested_by_name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="text.secondary">Participants</Typography>
                <Typography fontWeight={500}>{selectedRequest.estimated_participants}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="text.secondary">Preferred Date</Typography>
                <Typography fontWeight={500}>{selectedRequest.preferred_date || 'Not specified'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="text.secondary">Budget</Typography>
                <Typography fontWeight={500}>{selectedRequest.budget_range || 'Not specified'}</Typography>
              </Grid>
              {selectedRequest.special_requirements && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">Special Requirements</Typography>
                  <Typography>{selectedRequest.special_requirements}</Typography>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}><Box sx={{ borderTop: '1px solid #e2e8f0', pt: 2, mt: 1 }} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={requestUpdateForm.status || ''} label="Status" onChange={e => setRequestUpdateForm({ ...requestUpdateForm, status: e.target.value })}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth type="date" label="Confirmed Date" value={requestUpdateForm.confirmed_date || ''} onChange={e => setRequestUpdateForm({ ...requestUpdateForm, confirmed_date: e.target.value })} InputLabelProps={{ shrink: true }} size="small" />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth type="number" label="Confirmed Cost (₹)" value={requestUpdateForm.confirmed_cost || ''} onChange={e => setRequestUpdateForm({ ...requestUpdateForm, confirmed_cost: e.target.value })} size="small" />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Vendor Name" value={requestUpdateForm.vendor_name || ''} onChange={e => setRequestUpdateForm({ ...requestUpdateForm, vendor_name: e.target.value })} size="small" />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Vendor Contact" value={requestUpdateForm.vendor_contact || ''} onChange={e => setRequestUpdateForm({ ...requestUpdateForm, vendor_contact: e.target.value })} size="small" />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Expert Name" value={requestUpdateForm.expert_name || ''} onChange={e => setRequestUpdateForm({ ...requestUpdateForm, expert_name: e.target.value })} size="small" />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label="Venue / Meeting Link" value={requestUpdateForm.venue_or_link || ''} onChange={e => setRequestUpdateForm({ ...requestUpdateForm, venue_or_link: e.target.value })} size="small" />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label="Admin Notes" value={requestUpdateForm.admin_notes || ''} onChange={e => setRequestUpdateForm({ ...requestUpdateForm, admin_notes: e.target.value })} multiline rows={2} size="small" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRequestUpdate} disabled={requestUpdating}
            sx={{ bgcolor: '#06b6d4', '&:hover': { bgcolor: '#0891b2' } }}>
            {requestUpdating ? <CircularProgress size={20} /> : 'Update Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">{snackbar.message}</Alert>
      </Snackbar>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(d => ({ ...d, open: false }))} PaperProps={{ sx: { borderRadius: 3, minWidth: 360 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>{confirmDialog.title}</DialogTitle>
        <DialogContent><Typography>{confirmDialog.message}</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(d => ({ ...d, open: false })); }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminWellnessCalendar;
